'use client';

import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import type { DesktopNotificationNativeState } from '@/lib/desktop-notification-contract';
import {
  currentDesktopNotificationAvailability,
  currentDesktopNotificationBridge,
  type DesktopNotificationAvailability,
} from '@/lib/desktop-notifications';
import { resolveDesktopNotificationStatus } from '@/lib/notifications/desktop-notification-status';
import {
  hasBrowserNotificationOptOut,
  setBrowserNotificationOptOut,
  type BrowserNotificationPermissionStatus,
} from '@/lib/notifications/web-push-client';
import type { BrowserNotificationPermissionController } from './browser-notification-permission';

type DesktopSnapshot = {
  nativeState: DesktopNotificationNativeState | null;
  optedOut: boolean;
  operationPending: boolean;
};

let snapshot: DesktopSnapshot = {
  nativeState: null,
  optedOut: false,
  operationPending: false,
};
const listeners = new Set<() => void>();

function publish(next: Partial<DesktopSnapshot>) {
  const merged = { ...snapshot, ...next };
  if (
    merged.nativeState === snapshot.nativeState &&
    merged.optedOut === snapshot.optedOut &&
    merged.operationPending === snapshot.operationPending
  )
    return;
  snapshot = merged;
  listeners.forEach((listener) => listener());
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
const read = () => snapshot;
const noopSubscribe = () => () => undefined;
const browserAvailability = (): DesktopNotificationAvailability => 'browser';

/** Stable per page; the server render is always the website. */
export function useDesktopNotificationAvailability(): DesktopNotificationAvailability {
  return useSyncExternalStore(
    noopSubscribe,
    currentDesktopNotificationAvailability,
    browserAvailability
  );
}

/**
 * Native notification controller for the desktop app, or null on the
 * website. It reuses the browser opt-out key, so one switch governs both
 * native alerts and the page's own notifications.
 */
export function useDesktopNotificationPermission(
  active: boolean,
  availability: DesktopNotificationAvailability
): BrowserNotificationPermissionController | null {
  const shared = useSyncExternalStore(subscribe, read, read);
  const bridge = useMemo(
    () =>
      availability === 'desktop' ? currentDesktopNotificationBridge() : null,
    [availability]
  );
  const canOpenSettings =
    typeof navigator !== 'undefined' && !/\bLinux\b/.test(navigator.userAgent);

  const refresh = useCallback(async () => {
    let nativeState: DesktopNotificationNativeState | null = null;
    if (bridge) {
      try {
        nativeState = await bridge.getPermissionState();
      } catch {
        nativeState = 'unsupported';
      }
    }
    publish({ nativeState, optedOut: hasBrowserNotificationOptOut() });
    if (availability === 'browser') return 'unsupported' as const;
    return resolveDesktopNotificationStatus({
      availability,
      nativeState,
      optedOut: hasBrowserNotificationOptOut(),
      canOpenSettings,
    }).status;
  }, [availability, bridge, canOpenSettings]);

  useEffect(() => {
    if (!active || availability === 'browser') return;
    const onFocus = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    void refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active, availability, refresh]);

  const setOptOut = useCallback(
    async (optedOut: boolean): Promise<BrowserNotificationPermissionStatus> => {
      setBrowserNotificationOptOut(optedOut);
      publish({ operationPending: true });
      try {
        return await refresh();
      } finally {
        publish({ operationPending: false });
      }
    },
    [refresh]
  );

  if (availability === 'browser') return null;
  const resolved = resolveDesktopNotificationStatus({
    availability,
    nativeState: shared.nativeState,
    optedOut: shared.optedOut,
    canOpenSettings,
  });
  return {
    surface: 'desktop',
    status: resolved.status,
    unavailableReason: resolved.unavailableReason,
    settingsAction: resolved.action,
    configured: true,
    configurationPending: resolved.pending,
    operationPending: shared.operationPending,
    enable: () => setOptOut(false),
    disable: async () => {
      await setOptOut(true);
    },
    refresh: () => refresh(),
    reconcile: () => refresh(),
    openSystemSettings: async () => {
      if (!bridge) return false;
      try {
        return await bridge.openSettings();
      } catch {
        return false;
      }
    },
  };
}
